var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ProfileAvatar from "../../profileIdentity/ProfileAvatar";
import { getCompletedStays, getDisplayName } from "../../profileIdentity/profileIdentity.util";
import { ElementChip } from "../../uiElements/base/ElementChip";
import { ElementChipList } from "../../uiElements/base/ElementChipList";
var GUEST_STATUS_COLOR = '#2A9D97';
var CLUB_COLOR = '#B8901A';
var TOP_PERCENT_COLOR = '#6A44C9';
// Elite memberships without a level represent licence plates or A/R accounts.
var getClubMembership = function (profile) {
    var _a;
    return (_a = profile.memberships) === null || _a === void 0 ? void 0 : _a.find(function (membership) {
        var _a;
        var level = (_a = membership.membershipLevel) === null || _a === void 0 ? void 0 : _a.trim();
        return !!level && level !== '0';
    });
};
// topPercentileAll ranks lifetime revenue from the top (0 = biggest spender); only the top 10 % get a chip.
var getTopPercent = function (percentile) { return percentile === undefined || percentile === null
    ? undefined
    : [1, 5, 10].find(function (bucket) { return percentile < bucket; }); };
var HeaderChip = function (_a) {
    var text = _a.text, color = _a.color, Icon = _a.Icon, tooltip = _a.tooltip;
    return (_jsx(ElementChip, { text: text, startIcon: _jsx(Icon, { sx: { fontSize: 15, color: color } }), borderColor: alpha(color, 0.34), backgroundColor: alpha(color, 0.06), tooltipContent: tooltip }));
};
var iconButtonSx = { width: 36, height: 36, color: 'text.secondary' };
var ProfileHeader = function (_a) {
    var _b, _c, _d, _e;
    var profile = _a.profile, mode = _a.mode, onOpenFull = _a.onOpenFull, onRemove = _a.onRemove;
    var _f = useTranslation(), t = _f.t, i18n = _f.i18n;
    var _g = useState(null), menuAnchor = _g[0], setMenuAnchor = _g[1];
    var name = getDisplayName(profile);
    var completedStays = getCompletedStays(profile);
    var staysText = t('{{count}} stays', { count: completedStays });
    var guestStatus = (_b = profile.vipCode) === null || _b === void 0 ? void 0 : _b.trim();
    var clubMembership = getClubMembership(profile);
    var revenueStats = (_c = profile.smartData) === null || _c === void 0 ? void 0 : _c.revenueStats;
    var topPercent = getTopPercent(revenueStats === null || revenueStats === void 0 ? void 0 : revenueStats.topPercentileAll);
    var hasChips = !!(guestStatus || clubMembership || topPercent);
    var lifetimeRevenue = new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
    }).format((_d = revenueStats === null || revenueStats === void 0 ? void 0 : revenueStats.totalRevenue) !== null && _d !== void 0 ? _d : 0);
    var openFull = mode === 'simple' ? onOpenFull : undefined;
    var menuEntries = [
        openFull && {
            label: t('Open full profile'),
            Icon: OpenInFullIcon,
            onClick: openFull
        },
        mode === 'full' && {
            label: t('Open in System settings'),
            Icon: OpenInNewIcon,
            onClick: function () { return window.open("/system/profiles/".concat(profile.id), '_blank'); }
        },
        onRemove && {
            label: t('Remove from reservation'),
            Icon: PersonRemoveOutlinedIcon,
            onClick: onRemove,
            danger: true
        },
    ].filter(Boolean);
    var hasMenu = menuEntries.length > 0;
    return (_jsxs(Box, __assign({ sx: { position: 'relative' } }, { children: [_jsxs(Stack, __assign({ direction: 'row', alignItems: hasChips ? 'flex-start' : 'center', gap: '14px' }, (openFull ? {
                role: 'button',
                tabIndex: 0,
                'aria-label': "".concat(t('Open full profile'), ": ").concat(name),
                onClick: openFull,
                onKeyDown: function (event) {
                    if (event.key !== 'Enter' && event.key !== ' ')
                        return;
                    event.preventDefault();
                    openFull();
                }
            } : {}), { sx: openFull ? {
                    cursor: 'pointer',
                    borderRadius: '8px',
                    '&:hover .profile-header-name': { textDecoration: 'underline' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '4px' }
                } : undefined }, { children: [_jsx(ProfileAvatar, { profile: profile, size: 'large', completedStays: completedStays, completedStaysLabel: staysText }), _jsxs(Stack, __assign({ sx: { flex: 1, minWidth: 0, gap: '8px', pt: hasChips ? '4px' : 0, pr: hasMenu ? '32px' : 0 } }, { children: [_jsx(Typography, __assign({ className: 'profile-header-name', color: 'primary', sx: { fontSize: 24, fontWeight: 600, lineHeight: 1.15 } }, { children: name })), hasChips && (_jsxs(ElementChipList, { children: [guestStatus && (_jsx(HeaderChip, { text: guestStatus, color: GUEST_STATUS_COLOR, Icon: BadgeOutlinedIcon, tooltip: t('Guest status') })), clubMembership && (_jsx(HeaderChip, { text: clubMembership.membershipLevel.trim(), color: CLUB_COLOR, Icon: WorkspacePremiumOutlinedIcon, tooltip: ((_e = clubMembership.membershipProgram) === null || _e === void 0 ? void 0 : _e.trim()) || t('Club level') })), topPercent && (_jsx(HeaderChip, { text: t('Top {{percent}}%', { percent: topPercent }), color: TOP_PERCENT_COLOR, Icon: TrendingUpIcon, tooltip: "".concat(t('Lifetime revenue'), " ").concat(lifetimeRevenue, " \u00B7 ").concat(staysText) }))] }))] }))] })), hasMenu && (_jsx(IconButton, __assign({ "aria-label": t('More actions'), "aria-haspopup": 'menu', "aria-expanded": !!menuAnchor, onClick: function (event) { return setMenuAnchor(event.currentTarget); }, sx: __assign(__assign(__assign({}, iconButtonSx), { position: 'absolute', right: -6 }), (hasChips ? { top: 0 } : { top: '50%', transform: 'translateY(-50%)' })) }, { children: _jsx(MoreVertIcon, { sx: { fontSize: 20 } }) }))), _jsx(Menu, __assign({ anchorEl: menuAnchor, open: !!menuAnchor, onClose: function () { return setMenuAnchor(null); } }, { children: menuEntries.map(function (entry) { return (_jsxs(MenuItem, __assign({ onClick: function () {
                        setMenuAnchor(null);
                        entry.onClick();
                    }, sx: { color: entry.danger ? 'error.main' : undefined } }, { children: [_jsx(ListItemIcon, __assign({ sx: { color: entry.danger ? 'error.main' : undefined } }, { children: _jsx(entry.Icon, { fontSize: 'small' }) })), _jsx(ListItemText, { children: entry.label })] }), entry.label)); }) }))] })));
};
export default ProfileHeader;
